import { TFunction } from 'i18next';

import colours from 'theme/atoms/colours';

export interface UrgencyColorEntry {
  bg: string;
  border: string;
  text: string;
}

const informational: UrgencyColorEntry = {
  bg: colours.neutral[100],
  border: colours.neutral[200],
  text: colours.neutral[700],
};

const routine: UrgencyColorEntry = {
  bg: '#D9F2FF',
  border: colours.teal[100],
  text: colours.teal[800],
};

const needsAttention: UrgencyColorEntry = {
  bg: '#FFF3CF',
  border: colours.warning[100],
  text: colours.warning[800],
};

const emergency: UrgencyColorEntry = {
  bg: colours.primary[100],
  border: colours.primary[200],
  text: colours.primary[700],
};

/**
 * Keyed on the backend's language-independent urgency key, never on a
 * translated label. The API sends the key as `urgency_key` on events,
 * `category_key` on insights aggregates, and `priority` on event detail.
 *
 * Do not add translated strings here. A label-keyed map has to be re-synced
 * with the Django catalog every time a language is added, and silently drops
 * the colour when it is not.
 */
export const urgencyColorMap: Record<string, UrgencyColorEntry> = {
  Emergency: emergency,
  'Needs attention': needsAttention,
  Routine: routine,
  Informational: informational,
};

const URGENCY_LABEL_KEYS: Record<string, string> = {
  Emergency: 'urgency.emergency',
  'Needs attention': 'urgency.needsAttention',
  Routine: 'urgency.routine',
  Informational: 'urgency.informational',
};

export const URGENCY_KEY_ORDER = [
  'Emergency',
  'Needs attention',
  'Routine',
  'Informational',
];

export function getUrgencyColors(
  urgencyKey: string | null | undefined
): UrgencyColorEntry | null {
  if (!urgencyKey) return null;

  return urgencyColorMap[urgencyKey] ?? null;
}

/**
 * Localised label for an urgency key, resolved from the `shared` namespace so
 * every surface renders the same wording. Falls back to the raw key, which is
 * readable English, if the key is unrecognised.
 */
export function getUrgencyLabel(
  urgencyKey: string | null | undefined,
  t: TFunction
): string | null {
  if (!urgencyKey) return null;

  const labelKey = URGENCY_LABEL_KEYS[urgencyKey];
  if (!labelKey) return urgencyKey;

  return t(labelKey, { ns: 'shared' });
}
